import { useState, useRef, useEffect } from 'react';
import { Eye, EyeOff, Check, X } from 'lucide-react';
import { Button } from '../new-components/ui/button';
import { SecuritiesDisclaimer } from './SecuritiesDisclaimer';
import { InlineError } from './InlineError';

interface CreateLoginStepProps {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onContinue: () => void;
  onSwitchToSignIn?: () => void;
}

type Step1Field = 'firstName' | 'lastName' | 'email' | 'password';
type Step1Errors = Record<Step1Field, string>;

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (v: string) => v.length >= 8 },
  { label: 'One uppercase letter', test: (v: string) => /[A-Z]/.test(v) },
  { label: 'One lowercase letter', test: (v: string) => /[a-z]/.test(v) },
  { label: 'One number', test: (v: string) => /\d/.test(v) },
  { label: 'One special character (@$!%*?&)', test: (v: string) => /[@$!%*?&]/.test(v) },
];

export function CreateLoginStep({
  firstName,
  lastName,
  email,
  password,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPasswordChange,
  onContinue,
  onSwitchToSignIn,
}: CreateLoginStepProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [dirtyFields, setDirtyFields] = useState<Set<Step1Field>>(new Set());
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Desktop only: on touch devices, auto-focusing pops the keyboard on load and fights
    // the mobile layout. `pointer: fine` is true for mouse/trackpad, false for touch.
    if (typeof window !== 'undefined' && !window.matchMedia('(pointer: fine)').matches) return;
    const timer = setTimeout(() => {
      firstInputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const validateField = (field: Step1Field, value: string): string => {
    switch (field) {
      case 'firstName':
        return value.trim() ? '' : 'Please enter your first name.';
      case 'lastName':
        return value.trim() ? '' : 'Please enter your last name.';
      case 'email':
        if (!value.trim()) return 'Please enter your email.';
        if (!value.includes('@') || !value.includes('.')) return 'Please enter a valid email address.';
        return '';
      case 'password':
        if (!value) return 'Please enter a password.';
        if (!PASSWORD_RULES.every(r => r.test(value))) return 'Password does not meet all requirements.';
        return '';
    }
  };

  const errors: Step1Errors = {
    firstName: validateField('firstName', firstName),
    lastName: validateField('lastName', lastName),
    email: validateField('email', email),
    password: validateField('password', password),
  };

  const isValid = Object.values(errors).every(e => e === '');

  const markDirty = (field: Step1Field) => {
    setDirtyFields(prev => new Set(prev).add(field));
  };

  const shouldShowError = (field: Step1Field): boolean => {
    if (!errors[field]) return false;
    return attemptedSubmit || dirtyFields.has(field);
  };

  const getInputClassName = (hasError?: boolean) => {
    const base = 'w-full px-4 py-4 text-lg rounded-xl border-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none transition-colors';
    return hasError
      ? `${base} border-destructive focus:border-destructive`
      : `${base} border-border focus:border-primary`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAttemptedSubmit(true);
    if (isValid) {
      onContinue();
    }
  };

  const showPasswordHints = password.length > 0;

  return (
    <div className="flex flex-col flex-1 gap-6 md:block md:flex-none md:space-y-8">
      <div className="space-y-4 shrink-0">
        <h1 className="text-4xl md:text-5xl font-medium">Create account</h1>
        <p className="text-lg text-muted-foreground">Start with your name and a secure password.</p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col flex-1 gap-6 md:block md:gap-0 md:space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <input
                ref={firstInputRef}
                type="text"
                value={firstName}
                onChange={(e) => { onFirstNameChange(e.target.value); markDirty('firstName'); }}
                placeholder="First name"
                className={getInputClassName(shouldShowError('firstName'))}
                autoComplete="given-name"
              />
              {shouldShowError('firstName') && (
                <InlineError className="mt-1.5 text-sm">{errors.firstName}</InlineError>
              )}
            </div>
            <div>
              <input
                type="text"
                value={lastName}
                onChange={(e) => { onLastNameChange(e.target.value); markDirty('lastName'); }}
                placeholder="Last name"
                className={getInputClassName(shouldShowError('lastName'))}
                autoComplete="family-name"
              />
              {shouldShowError('lastName') && (
                <InlineError className="mt-1.5 text-sm">{errors.lastName}</InlineError>
              )}
            </div>
          </div>

          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => { onEmailChange(e.target.value); markDirty('email'); }}
              placeholder="Email address"
              className={getInputClassName(shouldShowError('email'))}
              autoComplete="email"
            />
            {shouldShowError('email') && (
              <InlineError className="mt-1.5 text-sm">{errors.email}</InlineError>
            )}
          </div>

          <div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { onPasswordChange(e.target.value); markDirty('password'); }}
                placeholder="Password"
                className={`${getInputClassName(shouldShowError('password'))} !pr-20`}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                // Prevent the tap from stealing focus/blurring the input — otherwise on mobile
                // the keyboard dismisses mid-tap, the layout shifts, and the click is cancelled
                // ("tapping does nothing"). Keeping focus makes the toggle fire reliably.
                onMouseDown={(e) => e.preventDefault()}
                className="absolute right-9 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-11 h-11 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {showPasswordHints && (
              <div className="mt-2 space-y-1">
                {PASSWORD_RULES.map((rule) => {
                  const passes = rule.test(password);
                  return (
                    <div key={rule.label} className="flex items-center gap-2 text-xs">
                      {passes ? (
                        <Check className="w-3.5 h-3.5 text-green-600" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-[#A0A0A0]" />
                      )}
                      <span className={passes ? 'text-green-600' : 'text-[#6B6B6B]'}>
                        {rule.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {onSwitchToSignIn && (
          <div className="text-center">
            <span className="text-muted-foreground">Already have an account? </span>
            <button
              type="button"
              onClick={onSwitchToSignIn}
              className="font-semibold text-foreground underline"
            >
              Sign In
            </button>
          </div>
        )}

        {/* Fine print, pushed to just above the CTA via mt-auto. Consent is center-aligned and
            scrolls with the content; the securities disclaimer shows on mobile (desktop uses
            the page footer). */}
        <div className="mt-auto space-y-2">
          <p className="text-xs text-muted-foreground text-center leading-relaxed [text-wrap:balance]">
            By continuing, you agree to the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="font-semibold text-foreground underline">Terms of Service</a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-foreground underline">Privacy Policy</a>, and consent to identity verification.
          </p>
          <SecuritiesDisclaimer className="md:hidden" />
        </div>

        {/* The ONLY sticky element — CTA is the bottommost item; it pins to the viewport
            bottom on mobile, inline on desktop. */}
        <div className="sticky bottom-0 z-10 bg-background pt-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:static md:z-auto md:bg-transparent md:pt-0 md:pb-0">
          <Button type="submit" className="w-full h-14 text-lg">
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
}
